import { memo, useState } from "react";
import styled, { css } from "styled-components";
import { RecordActionGroup } from "../../../../app/newLayout/page/components/PurchaseOrderDetails/RecordActionGroup";
import { useSummaryBarFields } from "../../../../hooks/useSummaryBarFields";
import { EditSummaryBarFields } from "./EditFields";
import { StyledDetailPageSummaryElement } from "./Detail.styles";
import { SummaryBarCell } from "./Cell";
import { SummaryBarCellBorder } from "./Border";
import { SummaryBarCellLabel } from "./Label";
import { SummaryBarCellValue } from "./Value";

const EditFieldsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="1.25" y="1.25" width="8.1" height="8.1" rx="1.6" fill="currentColor" />
    <rect x="10.85" y="1.25" width="8.1" height="8.1" rx="1.6" fill="currentColor" />
    <rect x="1.25" y="10.85" width="8.1" height="8.1" rx="1.6" fill="currentColor" />
    <rect x="10.85" y="10.85" width="8.1" height="8.1" rx="1.6" fill="currentColor" />
    <g transform="translate(17.7 17.7)">
      <circle className="editColumnsPlate" r="6.5" />
      <g fill="currentColor">
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" />
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" transform="rotate(45)" />
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" transform="rotate(90)" />
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" transform="rotate(135)" />
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" transform="rotate(180)" />
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" transform="rotate(225)" />
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" transform="rotate(270)" />
        <rect x="-1.05" y="-5.7" width="2.1" height="2.15" rx="0.45" transform="rotate(315)" />
        <circle r="3.55" />
        <circle className="editColumnsPlate" r="1.45" />
      </g>
    </g>
  </svg>
);

const SummaryBarCellMain = memo(({ label, value, isCustomValue = false, showBorder = true }) => (
  <>
    <SummaryBarCell>
      <SummaryBarCellLabel>{label}</SummaryBarCellLabel>
      {isCustomValue ? value : <SummaryBarCellValue>{value || "--"}</SummaryBarCellValue>}
    </SummaryBarCell>
    {showBorder && <SummaryBarCellBorder />}
  </>
));

const SummaryBarActionsSlot = styled.div`
  align-items: center;
  align-self: stretch;
  background: #f4f7fb;
  border-left: 1px solid var(--Color-Tokens-Border-Primary, #e4e4e4);
  border-bottom-right-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  flex-shrink: 0;
  gap: 6px;
  margin: -20px 0 -20px auto;
  padding: 0 16px;
  position: sticky;
  right: 0;

  button.editColumnsIcon.iconWrapper {
    --edit-columns-plate: #fff;

    .editColumnsPlate {
      fill: var(--edit-columns-plate);
    }

    &:hover {
      --edit-columns-plate: #f4f7ff;
    }
  }

  ${props =>
    props.$grid &&
    css`
      display: grid;
      grid-template-columns: repeat(3, 30px);
      grid-auto-rows: 30px;
      gap: 4px;
      align-content: center;
      padding: 6px 14px;

      > .btnGroup {
        display: contents;
      }

      .actionDivider {
        display: none;
      }

      .btnGroup.summaryActions .iconWrapper {
        width: 30px;
        height: 30px;
      }
    `}
`;

/**
 * DetailPageSummaryBar
 *
 * Renders summary bar cells. Pass `module`, `fields`, and `cells` for customizable bars;
 * otherwise renders `children` unchanged for legacy pages.
 */
const DetailPageSummaryBar = ({
  children,
  module,
  fields,
  cells,
  actions = null,
  actionsLayout = "row",
  prefix = null,
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const isConfigurable = Boolean(module && fields?.length && cells);

  const { visibleCells, selectedIds } = useSummaryBarFields(
    module,
    fields || [],
    cells || [],
  );

  const hasActionsSlot = isConfigurable || Boolean(actions);

  if (!isConfigurable) {
    return <StyledDetailPageSummaryElement>{children}</StyledDetailPageSummaryElement>;
  }

  return (
    <>
      <StyledDetailPageSummaryElement>
        {prefix ? (
          <>
            {prefix}
            {visibleCells.length > 0 && <SummaryBarCellBorder />}
          </>
        ) : null}
        {visibleCells.map(({ label, value, isCustomValue }, idx) => (
          <SummaryBarCellMain
            key={label}
            label={label}
            value={value}
            isCustomValue={isCustomValue}
            showBorder={idx < visibleCells.length - 1}
          />
        ))}
        {hasActionsSlot ? (
          <SummaryBarActionsSlot $grid={actionsLayout === "grid"}>
            {actions}
            {isConfigurable ? (
              <RecordActionGroup className="btnGroup summaryActions">
                {actions ? <span className="actionDivider" aria-hidden="true" /> : null}
                <button
                  type="button"
                  className="iconWrapper editColumnsIcon"
                  onClick={() => setIsEditOpen(true)}
                  title="Edit Summary Fields"
                  aria-label="Edit Summary Fields"
                >
                  <EditFieldsIcon />
                </button>
              </RecordActionGroup>
            ) : null}
          </SummaryBarActionsSlot>
        ) : null}
      </StyledDetailPageSummaryElement>
      <EditSummaryBarFields
        isOpen={isEditOpen}
        module={module}
        fields={fields}
        selectedIds={selectedIds}
        onClose={() => setIsEditOpen(false)}
      />
    </>
  );
};

export { DetailPageSummaryBar, SummaryBarActionsSlot };
